/**
 * DecisionsModal.tsx — the agent activity log modal.
 *
 * STRUCTURE COPIED FROM THE CHAT MODAL (src/chat/ChatModal.tsx +
 * src/chat/aui/Thread.tsx) — do not invent a parallel layout here:
 *   - the same Modal flags (`fullscreen` + `flushBody` + `hideHeader`),
 *     so the modal owns its own header;
 *   - `.aui-shell` = the sidebar + main split;
 *   - `.aui-sidebar` with the search row (`.aui-sidebar-search-field` —
 *     icon INSIDE the field) + `.aui-thread-row` rows; the chat's sidebar
 *     selects one conversation, ours MULTI-SELECTS agents (Check icon +
 *     aria-selected on the rows; "All agents" = empty selection);
 *   - `.aui-chat-header` — the 3-col grid: sidebar toggle (left, collapse
 *     persisted), the kind filter + search (center — where the chat keeps
 *     its model picker), close ✕ (right);
 *   - the body rows are the ToolFallback pattern VERBATIM
 *     (`.aui-tool-trigger` + chevron + `.aui-tool-body` +
 *     `.aui-tool-section-title` + `.aui-tool-pre`).
 *
 * Source-agnostic `entries` (`AgentActivityEntry[]`): the LIVE buffer
 * (`sim.decisionLog`) or the replay aggregation. `overlays.decisions`
 * remains only the per-tick cue channel, not this modal's source.
 */
import { useEffect, useMemo, useState } from 'react';
import { CaretDown, Check, Crosshair, MagnifyingGlass, SidebarSimple, X } from '@phosphor-icons/react';

import { Modal } from './Modal';
import type { AgentActivityEntry } from '../overlays/annotations';
import { agentColor } from '../graph/palette';
import { cx } from '../chat/aui/utils';

export interface DecisionsModalProps {
  /** Drives the animated overlay. The component must be ALWAYS MOUNTED (the
   *  ChatModal pattern): `isOpen` toggles the Modal's slide-up/down + fade,
   *  and the filters/selection/expanded-row state survives open/close. */
  isOpen: boolean;
  entries: AgentActivityEntry[];
  /** Resolve an agent identity key to a display name (bundle-aware). */
  agentName: (id: string) => string;
  nodeLabel: (id: string) => string;
  /** Jump the owning surface to a step (the replay tab's timeline scrub)
   *  and close the modal — the per-row ⌖ button. Absent in the live view
   *  (no timeline to scrub); the button hides. */
  onGoToStep?: (step: number) => void;
  onClose: () => void;
}

/** An agent group for the sidebar: the identity key (null = the model
 *  itself), its display name, and how many entries it has. Ordered by first
 *  appearance in the (oldest-first) entries array. Pure. */
export interface AgentGroup {
  key: string | null;
  label: string;
  count: number;
}

export function groupAgents(entries: AgentActivityEntry[], agentName: (id: string) => string): AgentGroup[] {
  const counts = new Map<string | null, number>();
  for (const e of entries) {
    counts.set(e.agent, (counts.get(e.agent) ?? 0) + 1);
  }
  return [...counts.entries()].map(([key, count]) => ({
    key,
    label: key === null ? 'Model' : agentName(key),
    count,
  }));
}

export type ActivityKindFilter = 'all' | 'decisions' | 'logs';

export interface ActivityFilter {
  /** Selected agent keys; empty = all agents (including the Model group). */
  agents: Set<string | null>;
  kind: ActivityKindFilter;
  search: string;
}

/** Does one entry pass the sidebar + toolbar filters? Case-insensitive; the
 *  search matches the message, rationale, agent display name, and node
 *  label. Pure. */
export function activityMatches(
  e: AgentActivityEntry,
  filter: ActivityFilter,
  name: { agentName: (id: string) => string; nodeLabel: (id: string) => string },
): boolean {
  if (filter.agents.size > 0 && !filter.agents.has(e.agent)) return false;
  if (filter.kind === 'decisions' && e.kind !== 'decision') return false;
  if (filter.kind === 'logs' && e.kind !== 'log') return false;
  const q = filter.search.trim().toLowerCase();
  if (!q) return true;
  const hay = [
    e.message,
    e.rationale ?? '',
    e.agent === null ? 'model' : name.agentName(e.agent),
    e.node ? name.nodeLabel(e.node) : '',
  ].join(' ').toLowerCase();
  return hay.includes(q);
}

/** `activityMatches` over an array (the test-facing form). Pure. */
export function filterActivityEntries(
  entries: AgentActivityEntry[],
  filter: ActivityFilter,
  name: { agentName: (id: string) => string; nodeLabel: (id: string) => string },
): AgentActivityEntry[] {
  return entries.filter((e) => activityMatches(e, filter, name));
}

const LEVEL_COLORS: Record<string, string> = {
  debug: 'var(--ink-muted)',
  info: 'var(--neutral)',
  warn: 'var(--warn, #e0a800)',
};

/** localStorage key for the sidebar collapse (separate from the chat's). */
const SIDEBAR_KEY = 'dec.sidebarCollapsed';

export function DecisionsModal({ isOpen, entries, agentName, nodeLabel, onGoToStep, onClose }: DecisionsModalProps): JSX.Element {
  // Entries arrive oldest-first (both sources append); a log glance wants the
  // newest on top without scrolling. Each carries its SOURCE index so row
  // keys stay stable regardless of the filters.
  const newestFirst = useMemo(() => entries.map((e, i) => ({ e, i })).reverse(), [entries]);
  const groups = useMemo(() => groupAgents(entries, agentName), [entries, agentName]);

  // Collapsible sidebar (the ChatModal pattern — persists across open/close;
  // the toggle stays in the header's left cell when collapsed).
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, sidebarCollapsed ? '1' : '0');
    } catch {
      /* storage unavailable — ignore */
    }
  }, [sidebarCollapsed]);

  const [selected, setSelected] = useState<Set<string | null>>(new Set());
  const [kind, setKind] = useState<ActivityKindFilter>('all');
  const [agentQuery, setAgentQuery] = useState('');
  const [search, setSearch] = useState('');
  const [openKey, setOpenKey] = useState<string | null>(null);

  const shownGroups = useMemo(
    () => (agentQuery.trim() ? groups.filter((g) => g.label.toLowerCase().includes(agentQuery.trim().toLowerCase())) : groups),
    [groups, agentQuery],
  );
  const filtered = useMemo(
    () => newestFirst.filter(({ e }) => activityMatches(e, { agents: selected, kind, search }, { agentName, nodeLabel })),
    [newestFirst, selected, kind, search, agentName, nodeLabel],
  );

  const toggleAgent = (key: string | null) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allAgents = selected.size === 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="fullscreen" flushBody hideHeader>
      <div className="aui-shell">
        {!sidebarCollapsed && (
          <aside className="aui-sidebar">
            <div className="aui-sidebar-search">
              <div className="aui-sidebar-search-field">
                <MagnifyingGlass size={14} className="aui-sidebar-search-icon" aria-hidden />
                <input
                  className="aui-sidebar-search-input"
                  type="text"
                  value={agentQuery}
                  onChange={(e) => setAgentQuery(e.target.value)}
                  placeholder="Search agents"
                  aria-label="Search agents"
                  spellCheck={false}
                />
              </div>
            </div>
            <div className="aui-sidebar-list">
              {/* "All agents" — active when nothing is selected. The check is
                  ALWAYS rendered (hidden when off) so the span is never
                  :empty — the chat's "New chat" ::before placeholder would
                  otherwise fire on unselected rows — and the width stays
                  fixed. */}
              <div className="aui-thread-row">
                <button
                  type="button"
                  className="aui-thread-row-trigger dec-agent-row"
                  aria-selected={allAgents}
                  onClick={() => setSelected(new Set())}
                >
                  <span className="dec-agent-check" aria-hidden>
                    <Check size={12} weight="bold" className={allAgents ? undefined : 'dec-check-off'} />
                  </span>
                  <span className="aui-thread-row-title">All agents</span>
                  {/* "All agents" stays neutral — no single colour applies. */}
                  <span className="dec-count-badge">{entries.length}</span>
                </button>
              </div>
              {shownGroups.map((g) => {
                const on = selected.has(g.key);
                return (
                  <div className="aui-thread-row" key={g.key ?? '__model'}>
                    <button
                      type="button"
                      className="aui-thread-row-trigger dec-agent-row"
                      aria-selected={on}
                      title={g.key ?? undefined}
                      onClick={() => toggleAgent(g.key)}
                    >
                      <span className="dec-agent-check" aria-hidden>
                        <Check size={12} weight="bold" className={on ? undefined : 'dec-check-off'} />
                      </span>
                      <span className="aui-thread-row-title">{g.label}</span>
                      {/* Color-coded badge: the agent's palette colour — the
                          SAME colour the timeline's decision marks use. */}
                      <span
                        className="dec-count-badge is-colored"
                        style={{ background: agentColor(g.key), borderColor: agentColor(g.key) }}
                      >
                        {g.count}
                      </span>
                    </button>
                  </div>
                );
              })}
              {shownGroups.length === 0 && <p className="aui-sidebar-empty">No agents match.</p>}
            </div>
          </aside>
        )}
        <main className="aui-main">
          {/* The chat header pattern: toggle (left) · filters (center) ·
              close (right) — the same 3-col grid. */}
          <div className="aui-chat-header">
            <div className="aui-chat-header-side aui-chat-header-left">
              <button
                type="button"
                className="aui-icon-btn"
                onClick={() => setSidebarCollapsed((v) => !v)}
                title={sidebarCollapsed ? 'Show agents' : 'Hide agents'}
                aria-label={sidebarCollapsed ? 'Show agents' : 'Hide agents'}
              >
                <SidebarSimple size={17} weight={sidebarCollapsed ? 'regular' : 'fill'} />
              </button>
            </div>
            <div className="aui-chat-header-center dec-toolbar">
              <div className="dec-seg" role="tablist" aria-label="Entry kind">
                {(['all', 'decisions', 'logs'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={`dec-seg-btn${kind === k ? ' is-active' : ''}`}
                    onClick={() => setKind(k)}
                  >
                    {k === 'all' ? 'All' : k === 'decisions' ? 'Decisions' : 'Logs'}
                  </button>
                ))}
              </div>
              <div className="dec-search">
                <MagnifyingGlass size={13} className="dec-search-icon" aria-hidden />
                <input
                  className="dec-search-input"
                  type="text"
                  placeholder="Search message, agent, node…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search activity"
                  spellCheck={false}
                />
              </div>
              <span className="dec-count">{filtered.length} entries</span>
            </div>
            <div className="aui-chat-header-side aui-chat-header-right">
              <button
                type="button"
                className="aui-icon-btn"
                onClick={onClose}
                title="Close activity log"
                aria-label="Close activity log"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          {/* The log body — ToolFallback's expandable-row pattern verbatim. */}
          <div className="dec-body">
            {filtered.length === 0 ? (
              <p className="dec-empty">
                No activity matches{entries.length === 0 ? ' — when the model or an agent acts, it shows up here' : ''}.
              </p>
            ) : (
              filtered.map(({ e, i }) => {
                const key = `${e.step}-${e.kind}-${i}`;
                const open = openKey === key;
                return (
                  <div key={key} className="aui-tool dec-row">
                    <div className="dec-row-line">
                      <button
                        type="button"
                        className="aui-tool-trigger dec-row-trigger"
                        aria-expanded={open}
                        onClick={() => setOpenKey(open ? null : key)}
                      >
                        <CaretDown
                          size={12}
                          className={cx('aui-tool-chevron', open && 'aui-tool-chevron-open')}
                          aria-hidden
                        />
                        <span className="dec-cell dec-cell-step">{e.step}</span>
                        <span className="dec-cell dec-cell-agent">{e.agent === null ? 'Model' : agentName(e.agent)}</span>
                        <span className="dec-cell dec-cell-kind">
                          {e.kind === 'decision' ? (
                            <span className="dec-kind dec-kind-decision">decision</span>
                          ) : (
                            <span className="dec-kind dec-kind-log" style={{ color: LEVEL_COLORS[e.level ?? 'info'] }}>
                              {e.level ?? 'info'}
                            </span>
                          )}
                        </span>
                        <span className="dec-cell dec-cell-msg">{e.message}</span>
                      </button>
                      {onGoToStep && (
                        <button
                          type="button"
                          className="btn-icon-mini dec-goto"
                          title="Show on timeline"
                          aria-label={`Show step ${e.step} on timeline`}
                          onClick={() => onGoToStep(e.step)}
                        >
                          <Crosshair size={13} />
                        </button>
                      )}
                    </div>
                    {open && (
                      <div className="aui-tool-body">
                        {e.rationale && (
                          <>
                            <div className="aui-tool-section-title">Rationale</div>
                            <div className="dec-detail-text">{e.rationale}</div>
                          </>
                        )}
                        {e.node && (
                          <>
                            <div className="aui-tool-section-title">Node</div>
                            <div className="dec-detail-text">{nodeLabel(e.node)}</div>
                          </>
                        )}
                        {e.refs && e.refs.length > 0 && (
                          <>
                            <div className="aui-tool-section-title">Refs</div>
                            <div className="dec-refs">
                              {e.refs.map((r, ri) => (
                                <span key={ri} className="dec-ref-chip" title={r.runId ? `run ${r.runId}` : 'no run yet'}>
                                  ⚡ {r.workflow}
                                  {r.runId ? ` · run ${r.runId}` : ''}
                                </span>
                              ))}
                            </div>
                          </>
                        )}
                        {e.data !== undefined && e.data !== null && (
                          <>
                            <div className="aui-tool-section-title">Data</div>
                            <pre className="aui-tool-pre">
                              {typeof e.data === 'string' ? e.data : JSON.stringify(e.data, null, 2)}
                            </pre>
                          </>
                        )}
                        <div className="aui-tool-section-title">Raw</div>
                        <pre className="aui-tool-pre">{JSON.stringify(e, null, 2)}</pre>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </main>
      </div>
    </Modal>
  );
}
